import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Divider,
  Chip,
  IconButton,
  Stack,
} from "@mui/material";
import {
  VolumeUp as AudioIcon,
  RecordVoiceOver as DialogueIcon,
} from "@mui/icons-material";
import QuestionRenderer from "./QuestionRenderer";
import PreviewAudioButton from "./PreviewAudioButton";

/**
 * DialogueGroupExerciseRenderer
 *
 * Renders dialogue group questions for exercise sessions (LC/RC exercises)
 * Features:
 * - Dialogue script with fillable blanks
 * - Audio playback button
 * - Child comprehension questions
 * - Modern, clean UI design
 */
const DialogueGroupExerciseRenderer = ({
  group,
  answers = {},
  onAnswerChange,
  showResult = false,
  disabled = false,
  // Optional: callback returning the display number for a child question.
  // When provided, overrides the default `question.number || index + 1`.
  getQuestionNumber,
  // Optional: per-child feedback map { [childId]: feedbackObject }.
  // When a child has a feedback entry, the child renders with showResult/disabled.
  feedbacks,
  // Optional: render-prop to display feedback UI below a child question.
  // Called as renderQuestionFeedback(childQuestion, feedback) => ReactNode.
  renderQuestionFeedback,
}) => {
  const [blankAnswers, setBlankAnswers] = useState({});
  const [audioPlaying, setAudioPlaying] = useState(false);

  // Extract parent question (dialogue) and child questions
  const parentQuestion =
    group.questions?.find((q) => q.type === "dialogue-group") ||
    group.dialogueQuestion;
  const childQuestions = group.childQuestions || [];

  // Initialize blank answers from props
  // For dialogue groups, answers are stored as: { blanks: { '1': 'answer' }, childQuestions: { 'qId': 'A' } }
  useEffect(() => {
    const dialogueAnswers = answers[`dialogue_${parentQuestion?._id}`];
    if (dialogueAnswers?.blanks) {
      setBlankAnswers(dialogueAnswers.blanks);
    } else {
      setBlankAnswers({});
    }
  }, [answers, parentQuestion]);

  // Handle blank input change
  const handleBlankChange = (position, value) => {
    const newBlanks = { ...blankAnswers, [position.toString()]: value };
    setBlankAnswers(newBlanks);

    if (onAnswerChange && parentQuestion) {
      // Get existing child question answers
      const dialogueKey = `dialogue_${parentQuestion._id}`;
      const existingDialogueAnswers = answers[dialogueKey] || {};
      const childQuestionAnswers = existingDialogueAnswers.childQuestions || {};

      // Update with new format: { blanks: {...}, childQuestions: {...} }
      onAnswerChange(
        dialogueKey,
        {
          blanks: newBlanks,
          childQuestions: childQuestionAnswers,
        },
        "dialogue-group",
      );
    }
  };

  // Handle child question answer change
  const handleChildQuestionChange = (questionId, answer) => {
    if (onAnswerChange && parentQuestion) {
      const dialogueKey = `dialogue_${parentQuestion._id}`;
      const existingDialogueAnswers = answers[dialogueKey] || {};
      const existingBlanks = existingDialogueAnswers.blanks || blankAnswers;

      // Update child question answers
      const updatedChildAnswers = {
        ...(existingDialogueAnswers.childQuestions || {}),
        [questionId]: answer,
      };

      // Update with new format: { blanks: {...}, childQuestions: {...} }
      onAnswerChange(
        dialogueKey,
        {
          blanks: existingBlanks,
          childQuestions: updatedChildAnswers,
        },
        "dialogue-group",
      );
    }
  };

  // Play audio
  const handlePlayAudio = () => {
    if (group.audioUrl || parentQuestion?.audioUrl) {
      const audio = new Audio(group.audioUrl || parentQuestion.audioUrl);
      setAudioPlaying(true);
      audio.play();
      audio.onended = () => setAudioPlaying(false);
    }
  };

  // Render dialogue script with inline input fields
  const renderDialogueScript = () => {
    const script = group.dialogueScript || parentQuestion?.dialogueScript;
    if (!script) return null;

    const blanks = group.blanks || parentQuestion?.blanks || [];
    const parts = [];
    let lastIndex = 0;
    const regex = /\{\{(\d+)\}\}/g;
    let match;

    // Split script by blank markers
    while ((match = regex.exec(script)) !== null) {
      const position = parseInt(match[1]);

      // Add text before blank
      if (match.index > lastIndex) {
        const textContent = script.substring(lastIndex, match.index);
        parts.push({
          type: "text",
          content: textContent,
        });
      }

      // Add blank input
      parts.push({
        type: "blank",
        position,
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < script.length) {
      parts.push({
        type: "text",
        content: script.substring(lastIndex),
      });
    }

    return (
      <Box
        sx={{
          lineHeight: 2.5,
          fontSize: "1.1rem",
          whiteSpace: "pre-wrap",
          backgroundColor: "#fafafa",
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          p: 3,
        }}
      >
        {parts.map((part, index) => {
          if (part.type === "text") {
            // Display content as-is with HTML support and preserve line breaks
            const contentWithBreaks = part.content.replace(/\n/g, "<br/>");
            return (
              <span
                key={index}
                dangerouslySetInnerHTML={{ __html: contentWithBreaks }}
              />
            );
          } else {
            // Render input for blank - same style as fill-blank questions
            const userAnswer = blankAnswers[part.position] || "";
            const blank = blanks.find((b) => b.position === part.position);
            const isCorrect =
              showResult &&
              blank?.correctAnswers?.some(
                (ans) =>
                  ans.toLowerCase().trim() === userAnswer.toLowerCase().trim(),
              );
            const isIncorrect = showResult && !isCorrect && userAnswer;

            return (
              <TextField
                key={index}
                value={userAnswer}
                onChange={(e) =>
                  handleBlankChange(part.position, e.target.value)
                }
                disabled={disabled || showResult}
                placeholder={`(${part.position})`}
                size="small"
                autoComplete="off"
                inputProps={{
                  autoComplete: "off",
                  autoCorrect: "off",
                  autoCapitalize: "off",
                  spellCheck: "false",
                  list: "autocompleteOff",
                  name: `dlg-blank-${parentQuestion?._id || "x"}-${part.position}`,
                }}
                sx={{
                  display: "inline-flex",
                  verticalAlign: "middle",
                  mx: "2px",
                  width: userAnswer
                    ? `${Math.max(
                        60,
                        Math.min(200, userAnswer.length * 12 + 30),
                      )}px`
                    : "80px",
                  maxWidth: "200px",
                  "& .MuiInputBase-root": {
                    backgroundColor: showResult
                      ? isCorrect
                        ? "#e8f5e9"
                        : isIncorrect
                        ? "#ffebee"
                        : "#f5f5f5"
                      : "white",
                    borderRadius: "4px",
                    fontSize: "1rem",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderWidth: "1px",
                    borderColor: showResult
                      ? isCorrect
                        ? "#4caf50 !important"
                        : isIncorrect
                        ? "#f44336 !important"
                        : "#bdbdbd !important"
                      : "#1976d2",
                  },
                  "& .MuiInputBase-input": {
                    padding: "4px 8px",
                    textAlign: "center",
                    fontSize: "1rem",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: disabled || showResult ? undefined : "#1565c0",
                  },
                }}
              />
            );
          }
        })}
      </Box>
    );
  };

  if (!parentQuestion) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        gap: 3,
        position: "relative",
        alignItems: "flex-start",
        flexWrap: { xs: "wrap", md: "nowrap" },
      }}
    >
      {/* Dialogue Script Section - Simple Style */}
      <Paper
        elevation={0}
        sx={{
          flex: { xs: "1 1 100%", md: "1 1 0" },
          minWidth: 0,
        }}
      >
        {/* Dialogue Script with Blanks */}
        {renderDialogueScript()}
      </Paper>

      {/* Child Questions Section */}
      {childQuestions.length > 0 && (
        <Box
          sx={{
            flex: { xs: "1 1 100%", md: "1 1 0" },
            minWidth: 0,
          }}
        >
          {childQuestions.map((question, index) => {
            // Get child question answer from dialogue answers structure
            const dialogueKey = `dialogue_${parentQuestion._id}`;
            const dialogueAnswers = answers[dialogueKey] || {};
            const childAnswer = dialogueAnswers.childQuestions?.[question._id];
            const childFeedback = feedbacks?.[question._id];
            // Once a child has feedback, lock it (disabled) but keep
            // showResult driven only by the parent prop — the feedback panel
            // below conveys correctness. This mirrors the standalone
            // multiple-choice behavior in trial-lab.
            const childDisabled = disabled || !!childFeedback;

            return (
              <Box key={question._id}>
                <QuestionRenderer
                  question={question}
                  answer={childAnswer}
                  onAnswerChange={(answer) =>
                    handleChildQuestionChange(question._id, answer)
                  }
                  showResult={showResult}
                  disabled={childDisabled}
                  questionNumber={
                    getQuestionNumber?.(question) ??
                    question.number ??
                    index + 1
                  }
                />
                {childFeedback && renderQuestionFeedback?.(question, childFeedback)}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default DialogueGroupExerciseRenderer;
